import permissionsManifest from "@/permissions.manifest.json";
import apiController from "@/utils/apiController";

/**
 * Audits screen ("Tela") permissions against the permissions manifest.
 *
 * For each screen the manifest declares a set of endpointKeys. Only the
 * endpoints classified as READ (endpoint.type === "READ") are considered
 * mandatory and are auto-added on refresh; WRITE/DELETE endpoints are left for
 * the user to add deliberately so read-only permissions are not edited wrongly.
 */

let indexByName = null;
let indexByCod = null;

// Mirrors the cod_recurso derivation used when screen ("Tela") resources are
// created (see resolveScreenResources in permissionResourceResolver.js), so a
// screen can still be matched when its `nome` is missing.
function deriveScreenCod(screenName) {
  return String(screenName).replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}

function buildScreenIndex() {
  if (indexByName) return;
  indexByName = new Map();
  indexByCod = new Map();
  for (const screen of permissionsManifest.screens || []) {
    const readCodes = new Set();
    for (const key of screen.endpointKeys || []) {
      const endpoint = apiController.endpoints[key];
      if (endpoint && endpoint.type === "READ" && endpoint.resource) {
        readCodes.add(endpoint.resource);
      }
    }
    const entry = { name: screen.name, readCodes };
    indexByName.set(screen.name, entry);
    indexByCod.set(deriveScreenCod(screen.name), entry);
  }
}

/**
 * Resolves a Tela resource to its manifest entry, matching by `nome` first and
 * falling back to `cod_recurso`.
 * @returns {{ name: string, readCodes: Set<string> }|null}
 */
function findScreenEntry(tela) {
  buildScreenIndex();
  if (tela.nome && indexByName.has(tela.nome)) {
    return indexByName.get(tela.nome);
  }
  if (tela.cod_recurso && indexByCod.has(tela.cod_recurso)) {
    return indexByCod.get(tela.cod_recurso);
  }
  return null;
}

/**
 * @returns {Set<string>|null} the resource codes (cod_recurso) of READ
 * endpoints the manifest suggests for a screen, or null if the screen is not
 * present in the manifest.
 */
export function getManifestReadCodes(screenName) {
  buildScreenIndex();
  const entry = indexByName.get(screenName);
  return entry ? entry.readCodes : null;
}

/**
 * Audits a single permission given the resources currently attached to it.
 *
 * @param {Array} resources resources returned by getRecursospermissao
 * @returns {{
 *   isScreen: boolean,
 *   screenName?: string,
 *   knownScreen?: boolean,
 *   missingReadCodes?: string[],
 *   outdated?: boolean,
 * }}
 */
export function auditScreenPermission(resources) {
  const list = Array.isArray(resources) ? resources : [];
  const tela = list.find((r) => r.descricao_tipo_recurso === "Tela");
  if (!tela) {
    return { isScreen: false };
  }

  const entry = findScreenEntry(tela);
  if (!entry) {
    return {
      isScreen: true,
      screenName: tela.nome || tela.cod_recurso,
      knownScreen: false,
      missingReadCodes: [],
      outdated: false,
    };
  }

  const currentCodes = new Set(
    list
      .filter((r) => r.descricao_tipo_recurso === "Endpoint")
      .map((r) => r.cod_recurso)
  );
  const missingReadCodes = [...entry.readCodes].filter((cod) => !currentCodes.has(cod));

  return {
    isScreen: true,
    screenName: entry.name,
    knownScreen: true,
    missingReadCodes,
    outdated: missingReadCodes.length > 0,
  };
}

/**
 * Runs an async mapper over items with a bounded concurrency so a large set of
 * per-permission requests does not flood the API at once.
 */
export async function runWithConcurrency(items, limit, mapper) {
  const results = new Array(items.length);
  let cursor = 0;

  async function worker() {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await mapper(items[index], index);
    }
  }

  const workers = [];
  for (let i = 0; i < Math.min(limit, items.length); i += 1) {
    workers.push(worker());
  }
  await Promise.all(workers);
  return results;
}
